import moment from 'moment';
import history from 'router/history';
import actions from 'redux-form/es/actions';
import * as actionTypes from 'actions/actionTypes';
import { getUserSession, startOneCentIdentification, resetLoginFlag } from 'actions/authentication';
import { track } from 'actions/analytics';
import { creditApplicationInitPath, generateConsent } from 'utils/helpers';
import { getMarketingConsents } from 'actions/marketingConsents';
import dateInAuNswTimezone from 'utils/dateInAuNswTimezone';
import getAnalyticsObjectAU from 'utils/analytics/getAnalyticsObjectAU';
import { API } from 'consts/api';
import { FORM_NAMES } from 'consts/formNames';

const { stopSubmit } = actions;

let otherServicesActivationAfterSuccess;
if (MOBILE_WALLET) {
  otherServicesActivationAfterSuccess = () => {
    history.push(`/other-services${location.search}${location.hash}`);
  };
}

export const getContractFreeze = (contractId) => ({
  types: [
    actionTypes.GET_CONTRACT_FREEZE,
    actionTypes.GET_CONTRACT_FREEZE_SUCCESS,
    actionTypes.GET_CONTRACT_FREEZE_FAIL,
  ],
  data: { contractId },
  promise: (client) => client.get(
    `${API_HOST}/contracts/${contractId}/extra-services/subscriptions/contractFreeze`,
    { hideError: true }
  ),
});

function getOutstandingAmount(types, contractId, date) {
  return {
    types,
    data: { contractId },
    promise: (client) => client.get(`${API_HOST}/contracts/${contractId}/outstanding-amount?forDate=${date}`, { hideError: true }),
  };
}

export function getProductPrincipal(contractId, date) {
  const types = [
    actionTypes.GET_PRODUCT_PRINCIPAL,
    actionTypes.GET_PRODUCT_PRINCIPAL_SUCCESS,
    actionTypes.GET_PRODUCT_PRINCIPAL_FAIL,
  ];
  return getOutstandingAmount(types, contractId, date);
}

export function getPrincipalBalance(contractId) {
  const types = [
    actionTypes.GET_PRINCIPAL_BALANCE,
    actionTypes.GET_PRINCIPAL_BALANCE_SUCCESS,
    actionTypes.GET_PRINCIPAL_BALANCE_FAIL,
  ];
  return getOutstandingAmount(types, contractId, dateInAuNswTimezone());
}

export const getContractData = (contractId, skipExtraServices, afterSuccess) => ({
  types: [
    actionTypes.GET_CONTRACT_DATA,
    actionTypes.GET_CONTRACT_DATA_SUCCESS,
    actionTypes.GET_CONTRACT_DATA_FAIL,
  ],
  data: { skipExtraServices },
  promise: (client) => {
    const contractReq = client.get(`${API_HOST}/contracts/${contractId}`, { hideError: true });
    const outstandingReq = client.get(`${API_HOST}/contracts/${contractId}/invoices/outstanding`, { hideError: true });
    const requests = [
      contractReq,
      outstandingReq,
    ];

    if (!skipExtraServices) {
      const extraServicesReq = client.get(`${API_HOST}/contracts/${contractId}/extra-services`, { hideError: true });
      if (COUNTRY === 'AU' || MOBILE_WALLET) {
        requests.push(extraServicesReq);
      } else {
        const extraServicesProductReq = extraServicesReq.then((response) => client.get(
          `${API_HOST}/contracts/${contractId}/extra-services/predicted/changeCreditLine?upgrade=true`,
          { hideError: true, contractExtraServices: response.data }
        ));
        requests.push(extraServicesProductReq);
      }
    }
    if (COUNTRY === 'CZ') {
      requests.push(
        client.get(`${API_HOST}/contracts/${contractId}/invoices/next`, { hideError: true })
      );
    }

    return Promise.all(requests);
  },
  afterSuccess: (dispatch, getState) => {
    const { authentication: { isJustLogedIn } } = getState();
    if (isJustLogedIn) {
      dispatch(resetLoginFlag());
      dispatch(track('loginSelfservice', {
        analyticsAU: (storeState) => getAnalyticsObjectAU('login_selfservice', storeState),
      }));
    }
    if (!skipExtraServices && MOBILE_WALLET) {
      dispatch(getContractFreeze(contractId));
    }
    if (typeof afterSuccess === 'function') {
      const { selfservice } = getState();
      afterSuccess(selfservice.contracts[contractId]);
    }
  },
  afterFailure: (dispatch, getState, response) => {
    const { data } = response;
    dispatch(track('reject', { product: 'getContractData', error: data }));
  },
});

export const getCreditApplication = () => ({
  types: [
    actionTypes.GET_CREDIT_APPLICATION,
    actionTypes.GET_CREDIT_APPLICATION_SUCCESS,
    actionTypes.GET_CREDIT_APPLICATION_FAILURE,
  ],
  promise: (client) => client.get(`${API_HOST}/${creditApplicationInitPath()}`, { hideError: true }),
  afterSuccess: (dispatch, getState, response) => {
    if (COUNTRY === 'FI') {
      // redirect to loan-app if there are any documentRequirements
      const { documentRequirements } = response.data;
      if (documentRequirements && documentRequirements.length > 0) {
        window.location.href = '/fi/apply/';
      }
    }
  },
});

export const getCreditApplicationStatus = (onSuccess) => ({
  types: [
    actionTypes.GET_CREDIT_APPLICATION_STATUS,
    actionTypes.GET_CREDIT_APPLICATION_STATUS_SUCCESS,
    actionTypes.GET_CREDIT_APPLICATION_STATUS_FAILURE,
  ],
  promise: (client) => client.get(
    `${API_HOST}/credit-application/checkstate`,
    { hideError: true }
  ),
  afterSuccess: (dispatch, getState, response) => {
    if (typeof onSuccess === 'function') {
      onSuccess(response.data);
    }
  },
});

export const getAccountStatement = (contractId, startDate, endDate) => {
  const from = startDate || moment().subtract(1, 'month').format('YYYY-MM-DD');
  const to = endDate || moment().format('YYYY-MM-DD');
  const URL = `${API_HOST}/contracts/${contractId}/account-statement?from=${from}&to=${to}`;
  return {
    types: [
      actionTypes.GET_ACCOUNT_STATEMENT,
      actionTypes.GET_ACCOUNT_STATEMENT_SUCCESS,
      actionTypes.GET_ACCOUNT_STATEMENT_FAIL,
    ],
    promise: (client) => client.get(URL, { contractId }, { hideError: true }),
    afterFailure: (dispatch, getState, response) => {
      const { data } = response;
      dispatch(track('reject', { product: 'getAccountStatement', error: data }));
    },
  };
};

export const getAccountStatementDetailed = (contractId, startDate, endDate) => {
  const from = startDate || moment().subtract(1, 'day').subtract(6, 'month').format('YYYY-MM-DD');
  const to = endDate || moment().subtract(1, 'day').format('YYYY-MM-DD');
  const URL = `${API_HOST}/contracts-cz/${contractId}/account-statement-detailed?from=${from}&to=${to}`;
  return {
    types: [
      actionTypes.GET_ACCOUNT_STATEMENT_DETAILED,
      actionTypes.GET_ACCOUNT_STATEMENT_DETAILED_SUCCESS,
      actionTypes.GET_ACCOUNT_STATEMENT_DETAILED_FAIL,
    ],
    data: { to },
    promise: (client) => client.get(URL, { contractId }, { hideError: true }),
  };
};

export const getOpenInvoice = (contractId) => {
  const URL = `${API_HOST}/contracts/${contractId}/invoices/open`;
  return {
    types: [
      actionTypes.GET_OPEN_INVOICE,
      actionTypes.GET_OPEN_INVOICE_SUCCESS,
      actionTypes.GET_OPEN_INVOICE_FAIL,
    ],
    promise: (client) => client.get(URL, { contractId }, { hideError: true }),
  };
};

export const getInvoices = (contractId) => {
  const URL = `${API_HOST}/contracts/${contractId}/invoices`;
  return {
    types: [
      actionTypes.GET_INVOICES,
      actionTypes.GET_INVOICES_SUCCESS,
      actionTypes.GET_INVOICES_FAIL,
    ],
    promise: (client) => client.get(URL, { contractId }, { hideError: true }),
    afterFailure: (dispatch, getState, response) => {
      const { data } = response;
      dispatch(track('reject', { product: 'getOpenInvoice', error: data }));
    },
  };
};

export const getLastInvoice = (contractId) => {
  const URL = `${API_HOST}/contracts/${contractId}/invoices/last`;
  return {
    types: [
      actionTypes.GET_LAST_INVOICE,
      actionTypes.GET_LAST_INVOICE_SUCCESS,
      actionTypes.GET_LAST_INVOICE_FAIL,
    ],
    promise: (client) => client.get(URL, { contractId }, { hideError: true }),
  };
};

export const getMmps = (contractId) => {
  const URL = `${API_HOST}/contracts/${contractId}/draw-selections`;
  return {
    types: [
      actionTypes.GET_MMPS,
      actionTypes.GET_MMPS_SUCCESS,
      actionTypes.GET_MMPS_FAIL,
    ],
    promise: (client) => client.get(URL, { contractId }, { hideError: true }),
    afterFailure: (dispatch, getState, response) => {
      const { data } = response;
      dispatch(track('reject', { product: 'getMmps', error: data }));
    },
  };
};

export function getMmpByDueDate(dueDate) {
  return {
    types: [
      actionTypes.GET_MMP_BY_NEW_DUE_DATE,
      actionTypes.GET_MMP_BY_NEW_DUE_DATE_SUCCESS,
      actionTypes.GET_MMP_BY_NEW_DUE_DATE_FAIL,
    ],
    promise: (client) => client.post(`${API_HOST}/credit-application/mmp`, { dueDate }),
  };
}

// const later = (delay, value) => new Promise((resolve) => setTimeout(resolve, delay, value));

export const sendDrawAmount = (contractId, amount) => {
  const URL = `${API_HOST}/contracts/${contractId}/draw`;
  return {
    types: [
      actionTypes.PUT_WITHDRAWAL,
      actionTypes.PUT_WITHDRAWAL_SUCCESS,
      actionTypes.PUT_WITHDRAWAL_FAIL,
    ],
    // promise: () => later(3000, amount),
    // eslint-disable-next-line max-len -- node upgrade
    promise: (client) => client.put(URL, { amount }, { contractId, hideError: true, preventLogout: true }),
    afterSuccess: (dispatch) => {
      dispatch(getContractData(contractId));
      dispatch(track('DrawCompletedSelfservice', { amount }));
    },
    afterFailure: (dispatch, getState, response) => {
      const { data } = response;
      dispatch(track('reject', { product: 'CL-DRAW', error: data }));
    },
  };
};

export const getBankName = (bankAccount) => {
  if (COUNTRY === 'FI' || COUNTRY === 'CZ') {
    if (bankAccount) {
      const URL = `${API_HOST}/customer/bankaccount/validation`;
      return {
        types: [
          actionTypes.GET_BANK_NAME,
          actionTypes.GET_BANK_NAME_SUCCESS,
          actionTypes.GET_BANK_NAME_FAIL,
        ],
        promise: (client) => client.post(URL, { bankAccount }, { hideError: true }),
      };
    }
  }

  const data = { action: 'getBankName' };
  if (DEBUG) {
    data.reason = 'no bank account provided or country is not supported';
  }
  return {
    data,
    type: actionTypes.ACTION_SKIPPED,
  };
};

export const setUpgradeHighlightOff = () => ({
  type: actionTypes.SET_UPGRADE_HIGHLIGHT_OFF,
});

export const saveMarketingConsents = (
  data,
  marketingConsents,
  locale,
  afterSuccess,
  afterFailure
) => {
  const requestBody = marketingConsents.reduce(
    (result, consent) => {
      const {
        consentName,
        dependantConsents = [],
        localizedVersions = {},
      } = consent;
      const consentVersion = localizedVersions[locale] || consent.version;
      let value = data[consentName] || false;
      const forceDependantConsentsToTrue = !consent.value && value;
      const additionalConsents = dependantConsents.map(
        (dependantConsent) => generateConsent(
          dependantConsent.consentName,
          forceDependantConsentsToTrue || data[dependantConsent.consentName] || false,
          consentVersion
        )
      );
      // eslint-disable-next-line max-len -- node upgrade
      const forceGeneralConsentValueToFalse = additionalConsents.length && additionalConsents.every((item) => (
        item.value === false
      ));
      if (forceGeneralConsentValueToFalse) {
        value = false;
      }
      return [
        ...result,
        ...additionalConsents,
        generateConsent(consentName, value, consentVersion),
      ];
    },
    []
  );
  return {
    types: [
      actionTypes.SAVE_MARKETING_CONSENTS,
      actionTypes.SAVE_MARKETING_CONSENTS_SUCCESS,
      actionTypes.SAVE_MARKETING_CONSENTS_FAIL,
    ],
    promise: (client) => client.put(
      `${API_HOST}/customer/marketing-consent`,
      requestBody
    ),
    afterSuccess: (dispatch) => {
      if (typeof afterSuccess === 'function') {
        afterSuccess();
      }
      dispatch(getMarketingConsents());
    },
    afterFailure: () => {
      if (typeof afterFailure === 'function') {
        afterFailure();
      }
    },
  };
};

export const changeCreditLine = (contractId, productId, consentsData, marketingConsents) => ({
  types: [
    actionTypes.PUT_NEW_CREDIT_LINE,
    actionTypes.PUT_NEW_CREDIT_LINE_SUCCESS,
    actionTypes.PUT_NEW_CREDIT_LINE_FAIL,
  ],
  promise: (client) => client.put(
    `${API_HOST}/contracts/${contractId}/extra-services/subscriptions/changeCreditLine`,
    { productId },
    { hideError: true, preventLogout: true }
  ),
  afterSuccess: (dispatch, getState) => {
    if (COUNTRY === 'FI') {
      dispatch(track('purchaseSelfservice'));
    }

    if (consentsData && marketingConsents) {
      dispatch(
        saveMarketingConsents(
          consentsData,
          marketingConsents,
          getState().intl.locale
        )
      );
    }

    if (!MOBILE_WALLET) {
      /*
        For mobile wallet user is sent away from our webview by native app, to dashboard,
        where they see their new limit; contracts will be updated when they return to our views.
      */
      history.replace('/status');
    }
  },
  afterFailure: (dispatch, getState, response) => {
    if (COUNTRY === 'FI' || COUNTRY === 'CZ') {
      const { data } = response;
      dispatch(track('reject', { product: 'CL-UPGRADE', error: data }));
    }
  },
});

export const startProfileEditing = (formId) => ({
  type: actionTypes.START_PROFILE_EDITING,
  data: { formId },
});

export const cancelProfileEditing = () => ({
  type: actionTypes.CANCEL_PROFILE_EDITING,
});

export const savePhoneNumber = (msisdn) => ({
  types: [
    actionTypes.SAVE_PHONE_NUMBER,
    actionTypes.SAVE_PHONE_NUMBER_SUCCESS,
    actionTypes.SAVE_PHONE_NUMBER_FAIL,
  ],
  promise: (client) => client.put(`${API_HOST}/customer/msisdn`, { msisdn }),
});

export const cancelUpdateData = () => ({
  type: actionTypes.CANCEL_UPDATE_DATA,
});

export const verifyMsisdn = (data, setIsModalOpen) => ({
  types: [
    actionTypes.VERIFY_MSISDN,
    actionTypes.VERIFY_MSISDN_SUCCESS,
    actionTypes.VERIFY_MSISDN_FAIL,
  ],
  promise: (client) =>
    client.post(API.CHANGE_MSISDN, data),
  afterSuccess: (dispatch) => {
    setIsModalOpen(false);
    dispatch(getUserSession());
  },
  afterFailure: (dispatch) => {
    dispatch(stopSubmit(FORM_NAMES.UPDATE_DATA, { otp: true }));
  },
});

export const updateContactsInfo = (
  data,
  otp,
  hasEmailChanged,
  setIsModalOpen
) => ({
  types: [
    actionTypes.UPDATE_CONTACT_INFO,
    actionTypes.UPDATE_CONTACT_INFO_SUCCESS,
    actionTypes.UPDATE_CONTACT_INFO_FAIL,
  ],
  promise: (client) => client.put(API.CHANGE_DATA, data),
  data: { hasEmailChanged },
  afterSuccess: (dispatch) => {
    if (otp) {
      const {
        msisdn: { msisdn },
      } = data;

      dispatch(verifyMsisdn({ msisdn, otp }, setIsModalOpen));
    } else {
      setIsModalOpen(false);
      dispatch(getUserSession());
    }
  },
});

export const saveContactsInfo = (data) => ({
  types: [
    actionTypes.SAVE_CONTACT_INFO,
    actionTypes.SAVE_CONTACT_INFO_SUCCESS,
    actionTypes.SAVE_CONTACT_INFO_FAIL,
  ],
  promise: (client) => {
    const requests = [];
    if (typeof data.deliveryMethodByEmail !== 'undefined') {
      const deliveryMethod = data.deliveryMethodByEmail ? 'EMAIL' : 'PAPER';
      const communicationSettingsData = {
        contractDeliveryMethod: deliveryMethod,
        invoiceDeliveryMethod: deliveryMethod,
      };
      requests.push(() => client.put(
        `${API_HOST}/customer/communication-settings`,
        communicationSettingsData
      ));
    }
    if (data.addressInput) {
      const {
        city, street, postcode, house,
      } = data;
      const streetStr = house ? `${street} ${house}` : street;
      requests.push(
        () => client.post(
          `${API_HOST}/customer-cz/registration-details/address`,
          { postcode, city, street: streetStr }
        )
      );
    }
    if (data.otp) {
      requests.push(
        () => client.post(
          `${API_HOST}/customer/msisdn/verification`,
          { msisdn: data.msisdn, otp: data.otp }
        )
      );
    }
    if (data.emailChanged) {
      requests.push(
        () => client.put(`${API_HOST}/customer/info`, { email: { email: data.email } })
      );
    }
    if (COUNTRY === 'CZ') {
      return requests.reduce((chain, curr) => chain.then(() => curr()), Promise.resolve());
    }
    return Promise.all(requests.map((request) => request()));
  },
  data: { emailChanged: data.emailChanged, email: data.email },
  afterSuccess: (dispatch) => {
    dispatch(getUserSession());
  },
  afterFailure: (dispatch, getState, error) => {
    if (data.otp) {
      let otpError = { otp: 'errors.field.otp' };
      if (error.data.errCode === 2902) {
        otpError = { ...otpError, expiredPin: 'errors.field.ExpiredPinCode' };
      }
      dispatch(
        stopSubmit('contacts', otpError)
      );
    }
  },
});

export const saveBankAccount = ({ bankAccount }) => ({
  types: [
    actionTypes.SAVE_BANK_ACCOUNT,
    actionTypes.SAVE_BANK_ACCOUNT_SUCCESS,
    actionTypes.SAVE_BANK_ACCOUNT_FAIL,
  ],
  promise: (client) => client.put(
    `${API_HOST}/customer/info`,
    { bankAccount: { bankAccount } }
  ),
  afterSuccess: (dispatch) => {
    dispatch(getUserSession());
    dispatch(getBankName(bankAccount));
  },
});

export const savePassword = ({ password }) => ({
  types: [
    actionTypes.SAVE_PASSWORD,
    actionTypes.SAVE_PASSWORD_SUCCESS,
    actionTypes.SAVE_PASSWORD_FAIL,
  ],
  promise: (client) => client.put(
    `${API_HOST}/customer/info`,
    { password: { password } }
  ),
});

export const resendConfirmationEmail = () => ({
  types: [
    actionTypes.RESEND_CONFIRMATION_EMAIL,
    actionTypes.RESEND_CONFIRMATION_EMAIL_SUCCESS,
    actionTypes.RESEND_CONFIRMATION_EMAIL_FAIL,
  ],
  promise: (client) => client.post(
    `${API_HOST}/customer/resend-email`,
    null,
    { hideError: true, preventLogout: true }
  ),
});

export const toggleDueDateReminder = (contractId, dueDateReminderService) => ({
  types: [
    actionTypes.TOGGLE_DUE_DATE_REMINDER,
    actionTypes.TOGGLE_DUE_DATE_REMINDER_SUCCESS,
    actionTypes.TOGGLE_DUE_DATE_REMINDER_FAIL,
  ],
  promise: (client) => {
    const URL = `${API_HOST}/contracts/${contractId}/extra-services/subscriptions/dueDateReminder`;
    const config = { hideError: true, preventLogout: true };
    if (dueDateReminderService.enabled) {
      return client.delete(URL, {}, config);
    }
    return client.put(URL, {}, config);
  },
  afterSuccess: (dispatch) => {
    dispatch(getContractData(contractId, false, otherServicesActivationAfterSuccess));
    if (COUNTRY === 'FI') {
      dispatch(track('DueDateReminder'));
    }
  },
  afterFailure: (dispatch, getState, response) => {
    if (COUNTRY === 'FI') {
      const { data } = response;
      dispatch(track('reject', { product: 'DueDateReminder', error: data }));
    }
  },
});

export const setUpgradeSuccessOff = () => ({
  type: actionTypes.SET_UPGRADE_SUCCESS_OFF,
});

export const getDueDateRange = (contractId) => ({
  types: [
    actionTypes.GET_DUE_DATE_RANGE,
    actionTypes.GET_DUE_DATE_RANGE_SUCCESS,
    actionTypes.GET_DUE_DATE_RANGE_FAIL,
  ],
  promise: (client) => client.get(
    `${API_HOST}/contracts/${contractId}/extra-services/subscriptions/duedate/range`
  ),
});

export const changeDueDate = (contractId, dueDate) => ({
  types: [
    actionTypes.CHANGE_DUE_DATE,
    actionTypes.CHANGE_DUE_DATE_SUCCESS,
    actionTypes.CHANGE_DUE_DATE_FAIL,
  ],
  promise: (client) => client.put(
    `${API_HOST}/contracts/${contractId}/extra-services/subscriptions/duedate`,
    { dueDate }
  ),
  afterSuccess: (dispatch) => {
    dispatch(getContractData(contractId, false, otherServicesActivationAfterSuccess));
    if (COUNTRY === 'FI' || COUNTRY === 'CZ') {
      dispatch(track('ChangeDueDate'));
    }
  },
  afterFailure: (dispatch, getState, response) => {
    if (COUNTRY === 'FI') {
      const { data } = response;
      dispatch(track('reject', { product: 'ChangeDueDate', error: data }));
    }
  },
});

export const takePaymentHoliday = (contractId) => ({
  types: [
    actionTypes.TAKE_PAYMENT_HOLIDAY,
    actionTypes.TAKE_PAYMENT_HOLIDAY_SUCCESS,
    actionTypes.TAKE_PAYMENT_HOLIDAY_FAIL,
  ],
  promise: (client) => client.put(
    `${API_HOST}/contracts/${contractId}/extra-services/subscriptions/paymentHoliday`
  ),
  afterSuccess: (dispatch) => {
    dispatch(getContractData(contractId, false, otherServicesActivationAfterSuccess));
    if (COUNTRY === 'FI' || COUNTRY === 'CZ') {
      dispatch(track('PaymentHoliday'));
    }
  },
  afterFailure: (dispatch, getState, response) => {
    if (COUNTRY === 'FI') {
      const { data } = response;
      dispatch(track('reject', { product: 'PaymentHoliday', error: data }));
    }
  },
});

export const getStatementOfAccount = (years, finalCall) => ({
  types: [
    actionTypes.GET_STATEMENT_OF_ACCOUNT,
    actionTypes.GET_STATEMENT_OF_ACCOUNT_SUCCESS,
    actionTypes.GET_STATEMENT_OF_ACCOUNT_FAIL,
  ],
  data: { finalCall },
  promise: (client) => {
    const requests = [];
    years.forEach((year) => {
      requests.push(client.get(`${API_HOST}/statement-of-account/year/${year}`, { year }));
    });

    return Promise.all(requests);
  },
  afterFailure: (dispatch, getState, response) => {
    const { data } = response;
    dispatch(track('reject', { product: 'getStatementOfAccount', error: data }));
  },
});

export function getAddressSuggestionsCZ(value, onSuccess) {
  return {
    types: [
      actionTypes.GET_ADDRESS_SUGGESTIONS,
      actionTypes.GET_ADDRESS_SUGGESTIONS_SUCCESS,
      actionTypes.GET_ADDRESS_SUGGESTIONS_FAIL,
    ],
    data: { type: 'address' },
    promise: (client) => client.post(
      `${API_HOST}/customer-cz/ruian-address-suggestion`,
      { userInput: value }
    ),
    afterSuccess: (dispatch, getState, responseData) => {
      if (typeof onSuccess === 'function') {
        const addressSuggestions = responseData.data.reduce(
          (result, suggestion) => ({
            ...result,
            [`${suggestion.street} ${suggestion.house}, ${suggestion.city}, ${suggestion.postcode}`]: suggestion,
          }),
          {}
        );
        onSuccess(addressSuggestions);
      }
    },
  };
}

function startKontomatikSession(id, signature) {
  return {
    types: [
      actionTypes.PUT_KONTOMATIK_SESSION,
      actionTypes.PUT_KONTOMATIK_SESSION_SUCCESS,
      actionTypes.PUT_KONTOMATIK_SESSION_FAIL,
    ],
    promise: (client) => client.put(
      `${API_HOST}/credit-application/kontomatik-session`,
      { id, signature },
      { hideError: true }
    ),
    afterSuccess: (dispatch) => {
      dispatch(getUserSession());
      history.replace('/profile');
    },
  };
}

export function putKontomatikSession(id, signature) {
  return (dispatch) => (
    dispatch(startOneCentIdentification(() =>
      (dispatch(startKontomatikSession(id, signature)))))
  );
}

export function getUpgradeEligibility(contractId) {
  return {
    types: [
      actionTypes.GET_UPGRADE_ELIGIBILITY,
      actionTypes.GET_UPGRADE_ELIGIBILITY_SUCCESS,
      actionTypes.GET_UPGRADE_ELIGIBILITY_FAIL,
    ],
    data: { contractId },
    promise: (client) => client.get(`${API_HOST}/upgrade-au/is-eligible?contract_id=${contractId}`),
    afterFailure: (dispatch, getState, response) => {
      const { data } = response;
      dispatch(track('reject', { product: 'getUpgradeEligibility', error: data }));
    },
  };
}

export function getUpsellEligibility(contractId) {
  return {
    types: [
      actionTypes.GET_UPSELL_ELIGIBILITY,
      actionTypes.GET_UPSELL_ELIGIBILITY_SUCCESS,
      actionTypes.GET_UPSELL_ELIGIBILITY_FAIL,
    ],
    data: { contractId },
    promise: (client) => client.get(`${API_HOST}/upsell-au/is-eligible?contract_id=${contractId}`),
    afterFailure: (dispatch, getState, response) => {
      const { data } = response;
      dispatch(track('reject', { product: 'getUpsellEligibility', error: data }));
    },
  };
}

export function applyIncrease(contractId, productType, increaseType) {
  const increaseURL = productType === 'INSTALLMENT' ? 'upsell' : 'upgrade';
  const increaseProductTypeName = productType === 'INSTALLMENT' ? 'IL-UPSELL' : 'CL-UPGRADE';

  return {
    types: [
      actionTypes.APPLY_INCREASE,
      actionTypes.APPLY_INCREASE_SUCCESS,
      actionTypes.APPLY_INCREASE_FAIL,
    ],
    data: { contractId },
    promise: (client) => client.post(`${API_HOST}/${increaseURL}-au/apply?contract_id=${contractId}`),
    afterSuccess: (dispatch) => {
      dispatch(track('checkoutSelfservice', { product: increaseProductTypeName }));
      window.location = `/au/apply-increase/?increaseType=${increaseType}`;
    },
    afterFailure: (dispatch, getState, response) => {
      const { data } = response;
      dispatch(track('reject', { product: increaseProductTypeName, error: data }));
    },
  };
}

export function getUpgradeApplication(contractId) {
  return {
    types: [
      actionTypes.GET_UPGRADE_APPLICATION,
      actionTypes.GET_UPGRADE_APPLICATION_SUCCESS,
      actionTypes.GET_UPGRADE_APPLICATION_FAIL,
    ],
    data: { contractId },
    promise: (client) => client.get(
      `${API_HOST}/contracts/${contractId}/upgrade/applicationWithOffer`,
      { hideError: true }
    ),
  };
}

export function confirmUpgradeApplication(contractId, approvedByCC) {
  return {
    types: [
      actionTypes.CONFIRM_UPGRADE_APPLICATION,
      actionTypes.CONFIRM_UPGRADE_APPLICATION_SUCCESS,
      actionTypes.CONFIRM_UPGRADE_APPLICATION_FAIL,
    ],
    data: { contractId },
    promise: (client) => client.post(
      `${API_HOST}/contracts/${contractId}/upgrade/${approvedByCC ? 'confirm' : 'upgrade'}`,
      { hideError: true }
    ),
  };
}

export function postUpgradeApplication(contractId, consentsData) {
  return {
    types: [
      actionTypes.POST_UPGRADE_APPLICATION,
      actionTypes.POST_UPGRADE_APPLICATION_SUCCESS,
      actionTypes.POST_UPGRADE_APPLICATION_FAIL,
    ],
    data: { contractId },
    promise: (client) => client.post(
      `${API_HOST}/contracts/${contractId}/upgrade/applicationWithOffer`
    ),
    afterSuccess: (dispatch, getState) => {
      const { selfservice, intl } = getState();
      const { upgradeApplication } = selfservice.contracts[contractId];
      if (
        upgradeApplication
        && upgradeApplication.state === 'OPEN'
        && upgradeApplication.documentRequirements
        && upgradeApplication.documentRequirements.length > 0
      ) {
        /*
          If documents are required, confirm request has to be executed in order to keep
          credit application on AIO side, otherwise AIO will "expire" it on next login
        */
        dispatch(confirmUpgradeApplication(contractId));
      }
      dispatch(
        saveMarketingConsents(
          consentsData,
          [{
            consentName: 'ASIAKASTIETO_DATAPOOL',
            version: 'FI_FI_C24_DPDDatapool_20180725',
          }],
          intl.locale
        )
      );
    },
  };
}
